import { Language, TranslatableText } from '@/types'

// ============================================================================
// Translation Utilities
// ============================================================================

/**
 * Get the translated text for a given language
 * Falls back through: requested language -> French -> English -> any available
 */
export function getTranslation(
  text: TranslatableText | string,
  lang: Language
): string {
  if (typeof text === 'string') {
    return text
  }

  // Try requested language
  if (text[lang]) {
    return text[lang]
  }

  // Fallback chain: FR -> EN -> AR
  if (text.fr) return text.fr
  if (text.en) return text.en
  if (text.ar) return text.ar

  // Last resort - return first available
  const firstAvailable = Object.values(text)[0]
  return firstAvailable || ''
}

/**
 * Get any available translation (for lists/search where language doesn't matter)
 */
export function getAnyTranslation(text: TranslatableText | string): string {
  if (typeof text === 'string') {
    return text
  }

  return text.fr || text.en || text.ar || ''
}

/**
 * Check if a text is translatable (has multiple languages)
 */
export function isTranslatable(text: TranslatableText | string): boolean {
  return typeof text !== 'string'
}

/**
 * Get all available languages for a translatable text
 */
export function getAvailableLanguages(text: TranslatableText | string): Language[] {
  if (typeof text === 'string') {
    return []
  }

  return Object.keys(text).filter((lang): lang is Language => {
    const value = text[lang as Language]
    return Boolean(value) && typeof value === 'string' && value.trim() !== ''
  })
}

/**
 * Format a translatable object with a new translation
 */
export function setTranslation(
  text: TranslatableText | string,
  lang: Language,
  value: string
): TranslatableText {
  const base = typeof text === 'string' ? { fr: text, ar: '', en: '' } : { ...text }
  return {
    ...base,
    [lang]: value,
  }
}
